import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

/** Cross-site transfer with multiple line items. Atomic on the backend. */
export default function TransferDialog({ open, onOpenChange, items, sites, onSuccess }) {
  const [source, setSource] = useState("");
  const [dest, setDest] = useState("");
  const [lines, setLines] = useState([{ item_id: "", quantity: "" }]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setSource(""); setDest(""); setNotes(""); setBusy(false);
    setLines([{ item_id: "", quantity: "" }]);
  };

  const updateLine = (i, patch) => {
    setLines((arr) => arr.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  };

  const submit = async () => {
    if (!source || !dest) return toast.error("Source and destination are required");
    if (source === dest) return toast.error("Source and destination must differ");
    const clean = lines
      .map((l) => ({ item_id: l.item_id, quantity: parseInt(l.quantity, 10) }))
      .filter((l) => l.item_id && l.quantity > 0);
    if (clean.length === 0) return toast.error("Add at least one valid line item");

    setBusy(true);
    try {
      const r = await api.post("/inventory/transfers", {
        source_site: source,
        destination_site: dest,
        line_items: clean,
        notes: notes.trim() || null,
      });
      reset();
      onOpenChange(false);
      onSuccess?.(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || "Transfer failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer stock between sites</DialogTitle>
          <DialogDescription>
            Multi-line, atomic transfer. If any line lacks stock, the whole transfer is rejected — nothing changes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="From (source)">
              <select data-testid="transfer-source" value={source} onChange={(e) => setSource(e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="To (destination)">
              <select data-testid="transfer-dest" value={dest} onChange={(e) => setDest(e.target.value)} className={inputCls}>
                <option value="">Select…</option>
                {sites.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="pt-1">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Line items</div>
              <button
                data-testid="transfer-add-line"
                onClick={() => setLines((a) => [...a, { item_id: "", quantity: "" }])}
                className="text-[11px] inline-flex items-center gap-1 text-olive font-semibold hover:underline"
              >
                <Plus size={11} strokeWidth={2.2} /> Add line
              </button>
            </div>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="transfer-lines">
              {lines.map((l, i) => {
                const picked = items.find((it) => it.item_id === l.item_id);
                return (
                <div
                  key={i}
                  data-testid={`transfer-line-${i}`}
                  className={"rounded-xl border px-2.5 py-2 " + (picked ? "border-olive/30 bg-olive/5" : "border-slate-200 bg-white")}
                >
                  {/* Resolved item label so the user always sees what is being moved */}
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="text-[11px] font-semibold text-slate-700 truncate" data-testid={`transfer-line-label-${i}`}>
                      {picked ? (
                        <>
                          <span className="text-olive">●</span> {picked.name}
                          <span className="text-slate-400 font-normal"> · {picked.unit}</span>
                        </>
                      ) : (
                        <span className="text-slate-400 font-normal italic">No item selected — pick one below</span>
                      )}
                    </div>
                    {lines.length > 1 && (
                      <button
                        data-testid={`transfer-line-remove-${i}`}
                        onClick={() => setLines((a) => a.filter((_, idx) => idx !== i))}
                        className="rounded-lg p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                      >
                        <Trash2 size={13} strokeWidth={1.8} />
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 items-center">
                    <select
                      data-testid={`transfer-line-item-${i}`}
                      value={l.item_id}
                      onChange={(e) => updateLine(i, { item_id: e.target.value })}
                      className={inputCls + " flex-1"}
                    >
                      <option value="">{picked ? "Change item…" : "Pick item…"}</option>
                      {items.map((it) => <option key={it.item_id} value={it.item_id}>{it.name} ({it.unit})</option>)}
                    </select>
                    <input
                      data-testid={`transfer-line-qty-${i}`}
                      type="number"
                      min="1"
                      placeholder="Qty"
                      value={l.quantity}
                      onChange={(e) => updateLine(i, { quantity: e.target.value })}
                      className={inputCls + " w-24"}
                    />
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          <Field label="Notes (optional)">
            <textarea data-testid="transfer-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls + " resize-none"} placeholder="e.g. Equipment moved for outdoor catering"/>
          </Field>
        </div>
        <DialogFooter className="mt-4">
          <button data-testid="transfer-cancel" onClick={() => onOpenChange(false)} className="rounded-full px-4 py-2 text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="transfer-submit" onClick={submit} disabled={busy} className="rounded-full px-4 py-2 text-xs font-semibold bg-olive text-white hover:opacity-90 disabled:opacity-50">{busy ? "Transferring…" : "Execute transfer"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30 w-full";
const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
